import {
  ANNUAL_RETENTION_DISCOUNT_PERCENTAGE_OFF,
  MONTHLY_RETENTION_DISCOUNT_PERCENTAGE_OFF,
  PriceDetails,
  UserData,
} from "../../constants/GlobalTypesAndEnumsAndConstants";
import { pageStore } from "../../zustand/globalStore";
// The Stripe prices and their formatting live in stripe-price-details.ts (shared with the marketing
// site's pricing page, which must not load the app's stores); this module adds the in-app cache.
import {
  buildCurrencyToLocaleMap,
  CURRENCY_MAP,
  fetchStripePrices,
  formatCurrencyMinorUnits,
  formatPrice,
  formatStripePriceDetails,
  getStripePriceDetailsForCountryCode,
  initializePrices,
} from "./stripe-price-details";
export { CURRENCY_MAP };

// ==========================
//  Monetization TYPES
// ==========================
// All types related to pricing, currency formatting and subscription plans live here to avoid bloating other utility files.
//

export type AppleSubscriptionProductDetails = {
  productId: string;
  localizedPrice: string;
  price: string;
  currency: string;
  discounts: { price: string }[];
  period_subscription: string;
  subscriptionPeriodUnitIOS: string;
};

export type GooglePlaySubscriptionProductDetails = {
  subscriptionOfferDetails: {
    pricingPhases: {
      pricingPhaseList: {
        recurrenceMode: 1 | 2;
        priceAmountMicros: string;
        billingCycleCount: number;
        billingPeriod: string;
        priceCurrencyCode: string;
        formattedPrice: string;
      }[];
    };
    offerToken: string;
    offerTags: string[];
    offerId: string | null;
    basePlanId: string;
  }[];
  name: string;
  productType: "subs";
  description: string;
  title: string;
  productId: string;
  platform: "android";
};

export const getStripePriceDetails = async (user: UserData) => {
  let priceDetails: PriceDetails = pageStore.getState().priceDetails;

  if (!priceDetails) {
    try {
      const countryCode = user?.country_code || "US";
      priceDetails = await initializePrices(countryCode);
      pageStore.getState().setPriceDetails(priceDetails);
    } catch {
      priceDetails = CURRENCY_MAP["US"];
    }
  }

  return formatStripePriceDetails(priceDetails);
};

/**
 * Extract and format Apple Store prices from the subscription data stored in Zustand.
 */
const getAppleStorePriceDetails = () => {
  let subscriptionsPriceDetails = pageStore.getState().appleSubscriptionPriceDetails || [];

  // let subscriptionsPriceDetails = JSON.parse(`[
  //   {
  //     "countryCode": "USA",
  //     "currency": "USD",
  //     "description": "Unlimited uploads, pages, and video minutes",
  //     "discounts": [],
  //     "introductoryPrice": "",
  //     "introductoryPriceAsAmountIOS": "",
  //     "introductoryPriceNumberOfPeriodsIOS": "",
  //     "introductoryPricePaymentModeIOS": "",
  //     "introductoryPriceSubscriptionPeriodIOS": "",
  //     "localizedPrice": "$11.99",
  //     "period_subscription": "MONTH",
  //     "platform": "ios",
  //     "price": "11.99",
  //     "productId": "super_learner_monthly",
  //     "subscriptionPeriodNumberIOS": "1",
  //     "subscriptionPeriodUnitIOS": "MONTH",
  //     "title": " Super Learner",
  //     "type": "subs"
  //   },
  //   {
  //     "countryCode": "USA",
  //     "currency": "USD",
  //     "description": "Monthly: 40 uploads, 100 pages, 60-min video",
  //     "discounts": [],
  //     "introductoryPrice": "",
  //     "introductoryPriceAsAmountIOS": "",
  //     "introductoryPriceNumberOfPeriodsIOS": "",
  //     "introductoryPricePaymentModeIOS": "",
  //     "introductoryPriceSubscriptionPeriodIOS": "",
  //     "localizedPrice": "$5.00",
  //     "period_subscription": "YEAR",
  //     "platform": "ios",
  //     "price": "59.99",
  //     "productId": "mega_mind_yearly",
  //     "subscriptionPeriodNumberIOS": "1",
  //     "subscriptionPeriodUnitIOS": "YEAR",
  //     "title": " Mega Mind",
  //     "type": "subs"
  //   },
  //   {
  //     "countryCode": "USA",
  //     "currency": "USD",
  //     "description": "Unlimited uploads, pages, and video minutes",
  //     "discounts": [],
  //     "introductoryPrice": "",
  //     "introductoryPriceAsAmountIOS": "",
  //     "introductoryPriceNumberOfPeriodsIOS": "",
  //     "introductoryPricePaymentModeIOS": "",
  //     "introductoryPriceSubscriptionPeriodIOS": "",
  //     "localizedPrice": "$10.00",
  //     "period_subscription": "YEAR",
  //     "platform": "ios",
  //     "price": "119.99",
  //     "productId": "super_learner_yearly",
  //     "subscriptionPeriodNumberIOS": "1",
  //     "subscriptionPeriodUnitIOS": "YEAR",
  //     "title": " Super Learner",
  //     "type": "subs"
  //   },
  //   {
  //     "countryCode": "USA",
  //     "currency": "USD",
  //     "description": "Monthly: 40 uploads, 100 pages, 60-min video",
  //     "discounts": [{}],
  //     "introductoryPrice": "",
  //     "introductoryPriceAsAmountIOS": "",
  //     "introductoryPriceNumberOfPeriodsIOS": "",
  //     "introductoryPricePaymentModeIOS": "",
  //     "introductoryPriceSubscriptionPeriodIOS": "",
  //     "localizedPrice": "$7.99",
  //     "period_subscription": "MONTH",
  //     "platform": "ios",
  //     "price": "7.99",
  //     "productId": "mega_mind_monthly",
  //     "subscriptionPeriodNumberIOS": "1",
  //     "subscriptionPeriodUnitIOS": "MONTH",
  //     "title": " Mega Mind",
  //     "type": "subs"
  //   }
  // ]`) as AppleSubscriptionProductDetails[];

  // Initialize priceDetails with defaults
  let priceDetails: PriceDetails = {
    symbol: "",
    monthlySuperLearnerPrice: "—",
    annualSuperLearnerPrice: "—",
    isMobileAppPrice: true,
    priceOf7DayTrial: null,
    currency: "",
  };

  const currencyRegex = /^([^0-9]+)?([\d,.]+)$/;

  // Extract Super Learner subscription data
  let superLearnerMonthlySubscription = null;
  let superLearnerAnnualSubscription = null;
  // NEW — Elite subscription data (parallel pass, strictly additive)
  let eliteMonthlySubscription = null;
  let eliteAnnualSubscription = null;

  subscriptionsPriceDetails.forEach((subscription) => {
    const { productId, localizedPrice, price, currency, period_subscription, subscriptionPeriodUnitIOS } = subscription;

    // Only process Super Learner subscriptions
    if (productId?.includes("super_learner")) {
      const isMonthly = subscriptionPeriodUnitIOS === "MONTH" || period_subscription === "MONTH";
      const isYearly = subscriptionPeriodUnitIOS === "YEAR" || period_subscription === "YEAR";

      const parsedPrice = localizedPrice.match(currencyRegex);
      const symbol = parsedPrice?.[1] || "";

      // Set currency and symbol from first Super Learner subscription found
      if (!priceDetails.symbol && symbol) {
        priceDetails.symbol = symbol;
      }
      if (!priceDetails.currency) {
        priceDetails.currency = currency;
      }

      if (isMonthly) {
        superLearnerMonthlySubscription = subscription;
        priceDetails.monthlySuperLearnerPrice = price; // Store numeric price for calculations
      } else if (isYearly) {
        superLearnerAnnualSubscription = subscription;
        priceDetails.annualSuperLearnerPrice = price; // Store numeric price for calculations
      }
    }

    // NEW — Process Elite subscriptions (SKUs: "elite_monthly" / "elite_yearly")
    if (productId?.includes("elite")) {
      const isMonthly = subscriptionPeriodUnitIOS === "MONTH" || period_subscription === "MONTH";
      const isYearly = subscriptionPeriodUnitIOS === "YEAR" || period_subscription === "YEAR";

      if (isMonthly) {
        eliteMonthlySubscription = subscription;
        priceDetails.monthlyElitePrice = price; // Store numeric price for calculations
      } else if (isYearly) {
        eliteAnnualSubscription = subscription;
        priceDetails.annualElitePrice = price; // Store numeric price for calculations
      }
    }
  });

  const formattedSuperLearnerMonthlyPrice = formatPrice(
    Number(priceDetails?.monthlySuperLearnerPrice || 0),
    priceDetails?.currency?.toString()
  );
  const formattedSuperLearnerPriceOfMonthlyIfPaidForFullYear = formatPrice(
    Number(priceDetails?.monthlySuperLearnerPrice || 0) * 12,
    priceDetails?.currency?.toString()
  );
  const formattedSuperLearnerAnnualPriceProratedMonthly = formatPrice(
    Number(priceDetails?.annualSuperLearnerPrice || 0) / 12,
    priceDetails?.currency?.toString()
  );

  const formattedSuperLearnerFullAnnualPrice = formatPrice(
    Number(priceDetails?.annualSuperLearnerPrice || 0),
    priceDetails?.currency?.toString()
  );

  // Calculate discount prices
  const superLearnerMonthlyDiscountPrice = priceDetails?.monthlySuperLearnerPrice
    ? formatPrice(
        Number(priceDetails.monthlySuperLearnerPrice) * (1 - MONTHLY_RETENTION_DISCOUNT_PERCENTAGE_OFF / 100),
        priceDetails?.currency?.toString()
      )
    : null;

  const superLearnerAnnualDiscountPrice = formatPrice(
    Number(priceDetails?.annualSuperLearnerPrice || 0) * (1 - ANNUAL_RETENTION_DISCOUNT_PERCENTAGE_OFF / 100),
    priceDetails?.currency?.toString()
  );

  // NEW — Format Elite prices from Apple IAP (null when no Elite SKUs in the product list)
  const formattedEliteMonthlyPrice = priceDetails?.monthlyElitePrice
    ? formatPrice(Number(priceDetails.monthlyElitePrice), priceDetails?.currency?.toString())
    : null;
  const formattedEliteFullAnnualPrice = priceDetails?.annualElitePrice
    ? formatPrice(Number(priceDetails.annualElitePrice), priceDetails?.currency?.toString())
    : null;
  const formattedEliteAnnualPriceProratedMonthly = priceDetails?.annualElitePrice
    ? formatPrice(Number(priceDetails.annualElitePrice) / 12, priceDetails?.currency?.toString())
    : null;
  const formattedElitePriceOfMonthlyIfPaidForFullYear = priceDetails?.monthlyElitePrice
    ? formatPrice(Number(priceDetails.monthlyElitePrice) * 12, priceDetails?.currency?.toString())
    : null;
  const eliteMonthlyDiscountPrice = priceDetails?.monthlyElitePrice
    ? formatPrice(
        Number(priceDetails.monthlyElitePrice) * (1 - MONTHLY_RETENTION_DISCOUNT_PERCENTAGE_OFF / 100),
        priceDetails?.currency?.toString()
      )
    : null;
  const eliteAnnualDiscountPrice = priceDetails?.annualElitePrice
    ? formatPrice(
        Number(priceDetails.annualElitePrice) * (1 - ANNUAL_RETENTION_DISCOUNT_PERCENTAGE_OFF / 100),
        priceDetails?.currency?.toString()
      )
    : null;

  // priceDetails keeps Apple's numeric prices ("11.99"): the yearly-saving note computes from them
  // (calculateAnnualDiscountPercentageFromRawPrices). Replacing them with the localized strings ("$11.99",
  // "11,99 €") made that computation NaN, so native iOS users always saw the fallback saving. Display uses the
  // formatted* fields above.

  return {
    formattedSuperLearnerMonthlyPrice,
    formattedSuperLearnerFullAnnualPrice,
    formattedSuperLearnerAnnualPriceProratedMonthly,
    formattedSuperLearnerPriceOfMonthlyIfPaidForFullYear,
    superLearnerMonthlyDiscountPrice,
    superLearnerAnnualDiscountPrice,
    superLearnerMonthlyProductId: superLearnerMonthlySubscription?.productId || null,
    superLearnerAnnualProductId: superLearnerAnnualSubscription?.productId || null,
    // NEW — Elite fields (null when no Elite SKUs in the Apple product list)
    formattedEliteMonthlyPrice,
    formattedEliteFullAnnualPrice,
    formattedEliteAnnualPriceProratedMonthly,
    formattedElitePriceOfMonthlyIfPaidForFullYear,
    eliteMonthlyDiscountPrice,
    eliteAnnualDiscountPrice,
    eliteMonthlyProductId: eliteMonthlySubscription?.productId || null,
    eliteAnnualProductId: eliteAnnualSubscription?.productId || null,
    priceDetails,
  };
};

/**
 * Extract and format Google Play prices from the subscription data stored in Zustand.
 */
const getGooglePlayPriceDetails = (offerId?: string | null) => {
  let subscriptionsPriceDetails = pageStore.getState().googlePlaySubscriptionPriceDetails;
  const currencyRegex = /^([^0-9]+)?([\d,.]+)$/;

  const getFormattedPrice = (price: string | undefined) => {
    if (!price) return null;
    const match = price.match(currencyRegex);
    return match ? match[2] : null;
  };

  const divideAnnualPriceBy12 = (price: string | undefined) => {
    if (!price) return null;
    const match = price.match(currencyRegex);
    if (!match) return null;
    const raw = parseFloat(match[2].replace(",", ""));
    return (raw / 12).toFixed(2);
  };

  const multiplyPriceBy12 = (price: string | undefined) => {
    if (!price) return null;
    const match = price.match(currencyRegex);
    if (!match) return null;
    const raw = parseFloat(match[2].replace(",", ""));
    return (raw * 12).toFixed(2);
  };

  // The price of one whole billing period as a plain number string ("11.99"), from the micros Google reports.
  // priceDetails holds these for calculations such as the yearly saving: the formatted strings follow the
  // store locale's separators, and the yearly one is divided by 12 for display.
  const getBillingPeriodPriceFromMicros = (priceAmountMicros: string | number | undefined): string | null => {
    const billingPeriodPrice = Number(priceAmountMicros) / 1_000_000;
    return billingPeriodPrice > 0 ? String(billingPeriodPrice) : null;
  };

  const result = {
    formattedSuperLearnerMonthlyPrice: null,
    formattedSuperLearnerFullAnnualPrice: null,
    formattedSuperLearnerAnnualPriceProratedMonthly: null,

    formattedSuperLearnerPriceOfMonthlyIfPaidForFullYear: null,

    superLearnerMonthlyDiscountPrice: null,
    superLearnerAnnualDiscountPrice: null,

    superLearnerMonthlyProductId: null,
    superLearnerAnnualProductId: null,

    // NEW — Elite fields (populated in the forEach below when elite- SKUs are found)
    formattedEliteMonthlyPrice: null,
    formattedEliteFullAnnualPrice: null,
    formattedEliteAnnualPriceProratedMonthly: null,
    formattedElitePriceOfMonthlyIfPaidForFullYear: null,
    eliteMonthlyDiscountPrice: null,
    eliteAnnualDiscountPrice: null,
    eliteMonthlyProductId: null,
    eliteAnnualProductId: null,

    priceDetails: {
      symbol: "",
      monthlySuperLearnerPrice: "",
      annualSuperLearnerPrice: "",
      isMobileAppPrice: true,
      priceOf7DayTrial: null,
      currency: "",
    } as PriceDetails,
  };

  let symbolDetected: string | null = null;
  let currencyDetected: string | null = null;

  // WISDOLIA-WEB-2ZSK: Google Play Billing returns subscriptionOfferDetails=null when
  // the SKU has no active offer for the user's locale; fall back to the empty defaults
  // already in `result` rather than crashing the upsell modal.
  if (!subscriptionsPriceDetails?.subscriptionOfferDetails) {
    return result;
  }

  subscriptionsPriceDetails.subscriptionOfferDetails.forEach((offer) => {
    const { offerId: currentOfferId, basePlanId, pricingPhases, offerToken } = offer;
    const pricePhaseList = pricingPhases.pricingPhaseList;
    const regularPrice = pricePhaseList.find((p) => p.recurrenceMode === 1);
    const promoPrice = pricePhaseList.find((p) => p.recurrenceMode === 2);

    const isTargetOffer = offerId != null && currentOfferId === offerId;
    const isBasePlan = currentOfferId == null;

    const isMonthly = basePlanId.includes("monthly");
    const isYearly = basePlanId.includes("yearly");
    const isSuperLearner = basePlanId.includes("super-learner");
    // NEW — Elite SKUs use "elite-monthly" / "elite-yearly" base plan IDs
    const isElite = basePlanId.includes("elite");

    const rawRegularFormatted = regularPrice?.formattedPrice || null;
    const regularBillingPeriodPrice = getBillingPeriodPriceFromMicros(regularPrice?.priceAmountMicros);
    const formattedRegularPrice = isYearly
      ? divideAnnualPriceBy12(rawRegularFormatted)
      : getFormattedPrice(rawRegularFormatted);

    if (regularPrice?.formattedPrice && !symbolDetected) {
      const currencyMatch = regularPrice.formattedPrice.match(currencyRegex);
      if (currencyMatch && currencyMatch[1]) {
        symbolDetected = currencyMatch[1];
        result.priceDetails.symbol = symbolDetected;
      }
    }

    if (regularPrice?.priceCurrencyCode && !currencyDetected) {
      currencyDetected = regularPrice.priceCurrencyCode;
      result.priceDetails.currency = currencyDetected;
    }

    if (isSuperLearner && isMonthly) {
      result.formattedSuperLearnerMonthlyPrice = formattedRegularPrice;
      if (isTargetOffer || (!offerId && isBasePlan)) {
        result.superLearnerMonthlyProductId = offerToken;
      }
      result.priceDetails.monthlySuperLearnerPrice = regularBillingPeriodPrice ?? "";
      result.formattedSuperLearnerPriceOfMonthlyIfPaidForFullYear = multiplyPriceBy12(rawRegularFormatted);
    } else if (isSuperLearner && isYearly) {
      result.formattedSuperLearnerAnnualPriceProratedMonthly = formattedRegularPrice;
      result.formattedSuperLearnerFullAnnualPrice = getFormattedPrice(rawRegularFormatted);
      if (isTargetOffer || (!offerId && isBasePlan)) {
        result.superLearnerAnnualProductId = offerToken;
      }
      // The full yearly price (formattedRegularPrice is the per-month figure).
      result.priceDetails.annualSuperLearnerPrice = regularBillingPeriodPrice ?? "";
    }

    // NEW — Elite plan detection, mirrors the Super Learner block above
    if (isElite && isMonthly) {
      result.formattedEliteMonthlyPrice = formattedRegularPrice;
      if (isTargetOffer || (!offerId && isBasePlan)) {
        result.eliteMonthlyProductId = offerToken;
      }
      result.formattedElitePriceOfMonthlyIfPaidForFullYear = multiplyPriceBy12(rawRegularFormatted);
      result.priceDetails.monthlyElitePrice = regularBillingPeriodPrice ?? undefined;
    } else if (isElite && isYearly) {
      result.formattedEliteAnnualPriceProratedMonthly = formattedRegularPrice;
      result.formattedEliteFullAnnualPrice = getFormattedPrice(rawRegularFormatted);
      result.priceDetails.annualElitePrice = regularBillingPeriodPrice ?? undefined;
      if (isTargetOffer || (!offerId && isBasePlan)) {
        result.eliteAnnualProductId = offerToken;
      }
    }

    if (isTargetOffer && promoPrice) {
      const formattedPromoPrice = getFormattedPrice(promoPrice.formattedPrice);
      if (isSuperLearner && isMonthly) {
        result.superLearnerMonthlyDiscountPrice = formattedPromoPrice;
      } else if (isSuperLearner && isYearly) {
        result.superLearnerAnnualDiscountPrice = formattedPromoPrice;
      }
      // NEW — Elite promo price (discount offer for Elite plan)
      if (isElite && isMonthly) {
        result.eliteMonthlyDiscountPrice = formattedPromoPrice;
      } else if (isElite && isYearly) {
        result.eliteAnnualDiscountPrice = formattedPromoPrice;
      }
    }
  });

  return result;
};

/**
 * Format currency symbol with country prefix for countries using "$" symbol
 * @param symbol - The currency symbol (e.g., "$")
 * @param currency - The currency code (e.g., "USD", "CAD", "MXN")
 * @returns The formatted symbol with prefix if needed (e.g., "CA$", "MX$")
 */
export const formatCurrencySymbol = (symbol: string | undefined, currency: string | undefined): string => {
  if (!symbol || !currency) return symbol || "";

  // Only process if symbol is "$"
  if (symbol !== "$") return symbol;

  // Map of currency codes to their prefixes
  const currencyPrefixMap: Record<string, string> = {
    CAD: "CA$", // Canadian Dollar
    MXN: "MX$", // Mexican Peso
    AUD: "AU$", // Australian Dollar
    NZD: "NZ$", // New Zealand Dollar
    SGD: "S$", // Singapore Dollar
    HKD: "HK$", // Hong Kong Dollar
    TWD: "NT$", // Taiwan Dollar
    ARS: "AR$", // Argentine Peso
    CLP: "CL$", // Chilean Peso
    COP: "CO$", // Colombian Peso
    UYU: "UY$", // Uruguayan Peso
    BRL: "R$", // Brazilian Real (special case)
    // USD doesn't need a prefix
  };

  return currencyPrefixMap[currency] || symbol;
};

// Export other helpers in case they are needed elsewhere.
export {
  initializePrices,
  getStripePriceDetailsForCountryCode,
  formatPrice,
  formatCurrencyMinorUnits,
  buildCurrencyToLocaleMap,
  getGooglePlayPriceDetails,
  getAppleStorePriceDetails,
  fetchStripePrices,
};
